import { useCallback, useEffect, useState } from "react";
import { DollarSign, History, LayoutList, PackageX, ScanLine, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { InventoryOverview } from "./InventoryOverview";
import { IntakeView } from "./IntakeView";
import { LedgerList } from "./LedgerList";
import { ShoppingListView } from "./ShoppingListView";
import { FoodCostView } from "./FoodCostView";
import { WasteView } from "./WasteView";
import { invApi } from "./api";

const VIEWS = [
  { key: "overview", label: "Overview", Icon: LayoutList },
  { key: "intake", label: "Scan", Icon: ScanLine },
  { key: "shopping", label: "Shop list", Icon: ShoppingCart },
  { key: "foodcost", label: "Food cost", Icon: DollarSign },
  { key: "waste", label: "Waste", Icon: PackageX },
  { key: "ledger", label: "History", Icon: History },
];

export function InventoryTab({ token, initialView = "overview", role = "admin" }) {
  const [view, setView] = useState(initialView);
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState([]);

  const reload = useCallback(async () => {
    try {
      const { data: d } = await invApi(token).get("/inventory/overview");
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load inventory");
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (view !== "ledger") return;
    invApi(token).get("/inventory/ledger", { params: { limit: 200 } }).then((r) => setLedger(r.data)).catch(() => {});
  }, [view, token, data]);

  return (
    <div className="space-y-4" data-testid="inventory-tab">
      <div className="flex flex-wrap gap-1 bg-white border border-stone-200 rounded-2xl p-1" data-testid="inventory-subnav">
        {VIEWS.map(({ key, label, Icon }) => {
          const badge = key === "overview" || key === "shopping" ? data?.low_count : 0;
          return (
            <button key={key} type="button" onClick={() => setView(key)} data-testid={`inventory-view-${key}`}
              className={`flex-1 min-w-fit px-2 py-2 rounded-xl text-[10px] sm:text-xs uppercase tracking-widest font-black flex items-center justify-center gap-1.5 whitespace-nowrap ${view === key ? "bg-brand-red text-white" : "text-stone-600 hover:bg-stone-50"}`}>
              <Icon className="w-4 h-4" /> <span>{label}</span>
              {badge > 0 && <span className={`text-[10px] px-1.5 rounded-full ${view === key ? "bg-white text-brand-red" : "bg-red-600 text-white"}`} data-testid={`inventory-low-badge-${key}`}>{badge}</span>}
            </button>
          );
        })}
      </div>
      {view === "overview" && <InventoryOverview token={token} data={data} reload={reload} role={role} onShop={() => setView("shopping")} />}
      {view === "intake" && <IntakeView token={token} onStockChanged={reload} />}
      {view === "shopping" && <ShoppingListView token={token} />}
      {view === "foodcost" && <FoodCostView token={token} />}
      {view === "waste" && <WasteView token={token} />}
      {view === "ledger" && (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200 font-black text-stone-800 text-sm uppercase tracking-widest">Stock movement log</div>
          <LedgerList token={token} rows={ledger} onChanged={reload} />
        </div>
      )}
    </div>
  );
}
